import { useMemo } from 'react';
import { List as ImmutableList } from 'immutable';
import { isReportTemplateWithSuiteNavData } from '../../types/reportTemplate';
import { RELEVANCY_DATA_SOURCES, RELEVANCY_FACTORS } from '../../types/relevancyData';
import { StandaloneTracker } from '../standaloneTracker';
import { getRelevancyDataFromDashboard, getReportsFromDashboard } from './userDataSourceUtils/dashboardBasedRecUtils';
import { isReportAlmostIdenticalToTemplate } from './reportUtils/reportCompareUtils';
import { getOverallRelevancyScore, useRelevancyDataFromDataSources } from './relevancyScoreUtils';
import { getDataTypeFamiliesFromReport } from './categorizationUtils/dataTypeFamilyDerivationUtils';
import { isHardToReproReport } from './reportUtils/getReportCharacteristics';
import { getDimensionCategoriesFromReport } from './categorizationUtils/dimensionCategorizationUtils';
import { getHubsFromReport } from './categorizationUtils/hubCategorizationUtils';
const RELEVANCY_FACTOR_WEIGHTS = {
  [RELEVANCY_FACTORS.DATA_TYPES]: 0.5,
  [RELEVANCY_FACTORS.SUB_SUITES]: 0.5,
  [RELEVANCY_FACTORS.HUBS]: 0.2,
  [RELEVANCY_FACTORS.DIMENSION_TYPES]: 0.1
};
const DATA_SOURCE_WEIGHTS = {
  [RELEVANCY_DATA_SOURCES.ORIGIN_DASHBOARD]: 0.7,
  [RELEVANCY_DATA_SOURCES.LAST_PAGE]: 0.7,
  [RELEVANCY_DATA_SOURCES.HOME_PAGE]: 0.5,
  [RELEVANCY_DATA_SOURCES.DERIVED_PERSONAS]: 0.4,
  [RELEVANCY_DATA_SOURCES.BOOKMARKS]: 0.4,
  [RELEVANCY_DATA_SOURCES.SIGNUP_SURVEY]: 0.2
};
export const getRecommendationDataFromReportTemplate = template => {
  const subSuiteId = isReportTemplateWithSuiteNavData(template) && template.getIn(['suiteNavData', 'subSuiteId']);
  const report = template;
  const dimensionCategories = getDimensionCategoriesFromReport(report);
  return {
    dataTypes: getDataTypeFamiliesFromReport(report),
    subSuites: subSuiteId ? [subSuiteId] : [],
    dimensionTypes: dimensionCategories,
    hubs: getHubsFromReport(report)
  };
};
export const sortReportTemplatesByRelevancy = (reportTemplates, allDataSourceRelevancyData) => {
  const reportTemplatesWithScores = reportTemplates.reduce((memo, template) => {
    const templateRecData = getRecommendationDataFromReportTemplate(template);
    const {
      score,
      contributingRelevancyData
    } = getOverallRelevancyScore(templateRecData, allDataSourceRelevancyData, RELEVANCY_FACTOR_WEIGHTS, DATA_SOURCE_WEIGHTS);
    return memo.push(template.set('relevancyScore', score).set('contributingRelevancyData', contributingRelevancyData));
  }, ImmutableList());
  const results = reportTemplatesWithScores.sort((templateA, templateB) => {
    const relevancyA = templateA.get('relevancyScore');
    const relevancyB = templateB.get('relevancyScore');
    if (relevancyA !== relevancyB) {
      return relevancyB - relevancyA;
    }
    if (isHardToReproReport(templateA)) {
      return -1;
    }
    if (isHardToReproReport(templateB)) {
      return 1;
    }
    return templateB.get('createdAt') - templateA.get('createdAt');
  });
  return ImmutableList(results);
};
export const useReportTemplateRecommendations = (reportTemplates, userInfo, additionalDataSourceInfo) => {
  const allRelevancyData = useRelevancyDataFromDataSources(userInfo);
  return useMemo(() => {
    const originDashboard = additionalDataSourceInfo === null || additionalDataSourceInfo === void 0 ? void 0 : additionalDataSourceInfo[RELEVANCY_DATA_SOURCES.ORIGIN_DASHBOARD];
    if (originDashboard) {
      const dashboardRelevancyData = getRelevancyDataFromDashboard(originDashboard);
      allRelevancyData.push(Object.assign({
        dataSource: RELEVANCY_DATA_SOURCES.ORIGIN_DASHBOARD
      }, dashboardRelevancyData));
    }
    if (reportTemplates.length === 0) {
      return ImmutableList([]);
    }
    return sortReportTemplatesByRelevancy(reportTemplates, allRelevancyData);
  }, [reportTemplates, allRelevancyData, additionalDataSourceInfo]);
};
export const trackReportRecommendationClick = template => {
  StandaloneTracker.track('Reporting', 'Analytics Interaction', Object.assign({
    action: 'clickAssetRecommendation',
    assetType: 'report',
    templateKey: template.get('templateKey')
  }, template.get('contributingRelevancyData')));
};
export const getTopRecommendationsWithExclusions = (recommendations, limit, getShouldExclude) => {
  let result = ImmutableList();
  for (let i = 0; i < recommendations.size && result.size < limit; i++) {
    const template = recommendations.get(i);
    if (!getShouldExclude(template)) {
      result = result.push(template);
    }
  }
  return result;
};
export const useTopRecomendationsWithDashboardContentsExclusions = (reportTemplates, limit, userInfo, dashboard) => {
  const additionalDataSourceInfo = useMemo(() => dashboard ? {
    [RELEVANCY_DATA_SOURCES.ORIGIN_DASHBOARD]: dashboard
  } : undefined, [dashboard]);
  const recommendations = useReportTemplateRecommendations(reportTemplates, userInfo, additionalDataSourceInfo);
  if (!dashboard) {
    return recommendations.take(limit);
  }
  const dashboardReports = getReportsFromDashboard(dashboard);
  const getShouldExclude = template => !!dashboardReports.find(report => isReportAlmostIdenticalToTemplate(report, template));
  return getTopRecommendationsWithExclusions(recommendations, limit, getShouldExclude);
};
export const __TESTABLE__ = {
  getRecommendationDataFromReportTemplate
};